import type { RuntimeAnswer, TaskAnswerEntry } from '../../types/runtime';

interface Props {
  answer: RuntimeAnswer;
}

function signalChip(kind: string | null | undefined) {
  if (kind === 'overdue') {
    return (
      <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-800">
        overdue
      </span>
    );
  }
  if (kind === 'due_soon') {
    return (
      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
        due soon
      </span>
    );
  }
  return null;
}

function priorityChip(priority: string | null | undefined) {
  if (!priority || priority === 'medium') return null;
  const colors: Record<string, string> = {
    urgent: 'bg-rose-100 text-rose-800',
    high: 'bg-orange-100 text-orange-800',
    low: 'bg-slate-100 text-slate-700',
  };
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${colors[priority] ?? 'bg-slate-100 text-slate-700'}`}
    >
      {priority}
    </span>
  );
}

function TaskRow({ task }: { task: TaskAnswerEntry }) {
  return (
    <li className="flex flex-wrap items-baseline gap-2 py-1">
      <span className="font-medium">{task.title ?? '(untitled)'}</span>
      {priorityChip(task.priority)}
      {signalChip(task.signal_kind)}
      {task.due_date && (
        <span className="text-xs text-amber-800">due {task.due_date.slice(0, 10)}</span>
      )}
    </li>
  );
}

export function TaskListCard({ answer }: Props) {
  const tasks = answer.tasks ?? [];
  const counts = answer.signal_counts ?? {};
  return (
    <article className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="rounded bg-amber-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
          Open tasks
        </span>
        {typeof answer.task_count === 'number' && (
          <span className="text-xs text-amber-800">
            {answer.task_count} total
            {counts.overdue ? `, ${counts.overdue} overdue` : ''}
            {counts.due_soon ? `, ${counts.due_soon} due soon` : ''}
          </span>
        )}
      </div>
      {tasks.length > 0 ? (
        <ul className="divide-y divide-amber-100">
          {tasks.map((t) => (
            <TaskRow key={t.task_id ?? t.title ?? Math.random().toString(36)} task={t} />
          ))}
        </ul>
      ) : (
        <p className="whitespace-pre-line leading-relaxed">{answer.content}</p>
      )}
    </article>
  );
}
